import { useCallback, useRef, useState } from 'react';
import {
  testHostReachability,
  TestHostReachabilityResponse,
} from '@/lib/api/devices/reachability';

export type ReachabilityStatus =
  | 'idle'
  | 'checking'
  | 'reachable'
  | 'unreachable'
  | 'untestable'
  | 'invalid'
  | 'error'
  | 'cancelled';

export interface UseUriReachabilityOptions {
  routeNamespaceId?: string;
  tenantId?: string;
  timeoutSeconds?: number;
}

export interface UseUriReachabilityReturn {
  status: ReachabilityStatus;
  result: TestHostReachabilityResponse | null;
  checkReachability: (uri: string) => void;
  cancel: () => void;
}

const isValidUri = (uri: string): boolean => {
  if (!uri || uri.trim().length === 0) return false;

  try {
    new URL(uri);
    return true;
  } catch {
    return false;
  }
};

export function useUriReachability(
  options: UseUriReachabilityOptions = {}
): UseUriReachabilityReturn {
  const [status, setStatus] = useState<ReachabilityStatus>('idle');
  const [result, setResult] = useState<TestHostReachabilityResponse | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  const cancel = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    setStatus('cancelled');
  }, []);

  const checkReachability = useCallback(
    async (uri: string) => {
      // Cancel any in-flight request
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      // Reset state if URI is empty
      if (!uri || uri.trim().length === 0) {
        setStatus('idle');
        setResult(null);
        return;
      }

      // Show invalid status if URI is malformed
      if (!isValidUri(uri)) {
        setStatus('invalid');
        setResult(null);
        return;
      }

      // Create new abort controller
      const abortController = new AbortController();
      abortControllerRef.current = abortController;

      setStatus('checking');
      setResult(null);

      try {
        const response = await testHostReachability(
          {
            uri,
            routeNamespaceId: options.routeNamespaceId,
            tenantId: options.tenantId,
            timeoutSeconds: options.timeoutSeconds,
          },
          abortController.signal
        );

        // Only update if this request wasn't cancelled
        if (!abortController.signal.aborted) {
          setResult(response);
          if (response.untestableReason) {
            setStatus('untestable');
          } else {
            setStatus(response.isReachable ? 'reachable' : 'unreachable');
          }
        }
      } catch (error) {
        // Only update if this request wasn't cancelled
        if (!abortController.signal.aborted) {
          if (error instanceof Error && error.name === 'AbortError') {
            setStatus('cancelled');
          } else {
            setStatus('error');
            setResult(null);
          }
        }
      }
    },
    [options.routeNamespaceId, options.tenantId, options.timeoutSeconds]
  );

  return {
    status,
    result,
    checkReachability,
    cancel,
  };
}
